// Replay viewer for a completed run. Walks recorded vitals_snapshots + events on a virtual clock,
// driving the existing MonitorPanel + PatientAvatar components. Scrub bar, play/pause, speed select,
// jump-to-next/prev event. Compact event log on the side highlights the current event.
//
// Read-only: no actions, no audio, no harm engine. Pure playback of recorded data.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import MonitorPanel, { type MonitorState } from '../components/Monitor/MonitorPanel';
import PatientAvatar from '../components/PatientCanvas/PatientAvatar';
import type { Rhythm } from '../lib/waveforms';

interface Vitals {
  t_ms: number;
  rhythm: string;
  hr: number;
  sbp: number;
  dbp: number;
  spo2: number;
  rr: number;
  etco2_x10: number;
  temp_c_x10: number;
  cpr_active: boolean;
  cpr_quality_pct: number;
  current_phase: string;
}

interface EventRow {
  t_ms: number;
  action_type: string;
  actor_role?: string | null;
  action_payload_json?: any;
}

const SPEEDS = [0.5, 1, 2, 4, 8] as const;

const ACTION_LABEL: Record<string, string> = {
  cpr_pulse: 'CPR pulse',
  start_compressions: 'Start CPR',
  stop_compressions: 'Stop CPR',
  rhythm_check: 'Rhythm check',
  pulse_check_carotid: 'Pulse check',
  charge_defib: 'Charge defib',
  shock: 'Shock',
  drug_admin: 'Drug',
  insert_igel: 'i-gel inserted',
  bvm_ventilation: 'BVM breath',
  call_for_help: 'Called for help',
  declare_4hs_4ts: 'Declared 4Hs/4Ts',
  team_callout: 'Closed-loop callout',
  system_harm_event: 'HARM EVENT',
  system_rhythm_change: 'Rhythm degenerated',
  system_deterioration: 'Deteriorated',
};

function isRhythm(v: string | undefined): v is Rhythm {
  return !!v && ['sinus','sinus_tach','brady','af','svt','vt_pulse','vt_pulseless','vf','torsades','asystole','pea_narrow','pea_broad','chb'].includes(v);
}

export default function Replay() {
  const { run_id } = useParams();
  const [searchParams] = useSearchParams();
  const initialT = Number(searchParams.get('t') || 0);

  const [data, setData] = useState<{ vitals: Vitals[]; events: EventRow[]; scenario: any; run: any } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [currentT, setCurrentT] = useState(initialT);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(1);
  const lastFrameRef = useRef<number>(0);
  const animRef = useRef<number | null>(null);

  useEffect(() => {
    if (!run_id) return;
    api.runById(run_id)
      .then((r) => setData({ vitals: r.vitals || [], events: r.events || [], scenario: r.scenario, run: r.run }))
      .catch((e) => setErr(e.message || 'Failed to load run'));
  }, [run_id]);

  const duration = useMemo(() => {
    if (!data) return 0;
    const lastV = data.vitals.length ? data.vitals[data.vitals.length - 1]!.t_ms : 0;
    const lastE = data.events.length ? data.events[data.events.length - 1]!.t_ms : 0;
    return Math.max(lastV, lastE, 1);
  }, [data]);

  // Find vitals snapshot active at currentT (last snapshot with t_ms <= currentT)
  const activeVitals = useMemo(() => {
    if (!data || data.vitals.length === 0) return null;
    let lo = 0, hi = data.vitals.length - 1, found = data.vitals[0]!;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const v = data.vitals[mid]!;
      if (v.t_ms <= currentT) { found = v; lo = mid + 1; } else { hi = mid - 1; }
    }
    return found;
  }, [data, currentT]);

  // Events that have occurred up to currentT (most recent last)
  const pastEvents = useMemo(() => {
    if (!data) return [];
    return data.events.filter((e) => e.t_ms <= currentT);
  }, [data, currentT]);

  // Event currently being highlighted (the most recent one within ±3s)
  const focusEvent = useMemo(() => {
    if (!data) return null;
    let best: EventRow | null = null;
    let bestDt = Infinity;
    for (const e of data.events) {
      const dt = Math.abs(e.t_ms - currentT);
      if (dt < bestDt) { bestDt = dt; best = e; }
    }
    return bestDt < 3000 ? best : null;
  }, [data, currentT]);

  // Playback loop
  useEffect(() => {
    if (!playing) {
      if (animRef.current != null) cancelAnimationFrame(animRef.current);
      animRef.current = null;
      return;
    }
    lastFrameRef.current = 0;
    function tick(now: number) {
      if (lastFrameRef.current === 0) lastFrameRef.current = now;
      const dt = now - lastFrameRef.current;
      lastFrameRef.current = now;
      setCurrentT((t) => {
        const next = t + dt * speed;
        if (next >= duration) { setPlaying(false); return duration; }
        return next;
      });
      animRef.current = requestAnimationFrame(tick);
    }
    animRef.current = requestAnimationFrame(tick);
    return () => { if (animRef.current != null) cancelAnimationFrame(animRef.current); };
  }, [playing, speed, duration]);

  function jumpToEvent(direction: 1 | -1) {
    if (!data) return;
    const sorted = data.events.slice().sort((a, b) => a.t_ms - b.t_ms);
    if (direction === 1) {
      const next = sorted.find((e) => e.t_ms > currentT + 50);
      if (next) setCurrentT(next.t_ms);
    } else {
      const prev = [...sorted].reverse().find((e) => e.t_ms < currentT - 50);
      if (prev) setCurrentT(prev.t_ms);
    }
  }

  if (err) return <div className="max-w-4xl mx-auto p-8 text-alarm-red">{err}</div>;
  if (!data) return <div className="max-w-4xl mx-auto p-8 text-mono-white/60">Loading replay…</div>;

  // Build a synthetic MonitorState from the active snapshot
  const monitorState: MonitorState = activeVitals ? {
    rhythm: isRhythm(activeVitals.rhythm) ? activeVitals.rhythm : 'sinus',
    hr: activeVitals.hr,
    sbp: activeVitals.sbp,
    dbp: activeVitals.dbp,
    spo2: activeVitals.spo2,
    rr: activeVitals.rr,
    etco2_mmhg: activeVitals.etco2_x10 / 10,
    temp_c: activeVitals.temp_c_x10 / 10,
    sim_t_ms: currentT,
    cpr_active: !!activeVitals.cpr_active,
    cpr_quality_pct: activeVitals.cpr_quality_pct,
    cpr_rate_cpm: 0, // historical rate not stored in vitals_snapshots; pleth derives from snapshot HR
    intubated: false,
  } : {
    rhythm: 'sinus' as Rhythm, hr: 0, sbp: 0, dbp: 0, spo2: 0, rr: 0, etco2_mmhg: 0, temp_c: 37,
    sim_t_ms: 0, cpr_active: false, cpr_quality_pct: 0, cpr_rate_cpm: 0, intubated: false,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
        <div>
          <p className="text-xs text-mono-white/40 uppercase tracking-widest">Replay · read-only</p>
          <h1 className="text-2xl font-semibold mt-1">{data.scenario?.title || 'Run'}</h1>
          <p className="text-mono-white/50 text-sm mt-1">
            {data.scenario?.region} · {data.run?.difficulty} · {data.run?.outcome || 'in progress'}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to={`/debrief/${run_id}`} className="btn-ghost text-sm">← Back to debrief</Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-4 mb-4">
        <div className="lg:col-span-3">
          <PatientAvatar
            rhythm={monitorState.rhythm}
            hr={monitorState.hr}
            spo2={monitorState.spo2}
            cpr_active={monitorState.cpr_active}
            cpr_quality_pct={monitorState.cpr_quality_pct}
            airway="natural"
            current_phase={activeVitals?.current_phase || 'arrest'}
            intubated={monitorState.intubated}
          />
        </div>
        <div className="lg:col-span-2">
          <MonitorPanel state={monitorState} />
        </div>
      </div>

      {/* Playback controls */}
      <div className="panel p-4 mb-4">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <button onClick={() => jumpToEvent(-1)} className="btn-ghost text-sm" title="Previous event">⏮ Prev event</button>
          <button onClick={() => setPlaying(!playing)} className="btn-primary text-sm w-24">{playing ? '⏸ Pause' : '▶ Play'}</button>
          <button onClick={() => jumpToEvent(1)} className="btn-ghost text-sm" title="Next event">Next event ⏭</button>
          <div className="flex gap-1 ml-2">
            {SPEEDS.map((s) => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`text-xs font-mono px-2 py-1 rounded ${speed === s ? 'bg-brand-600 text-white' : 'bg-ink-800 hover:bg-ink-950 text-mono-white/70'}`}
              >{s}×</button>
            ))}
          </div>
          <div className="ml-auto font-mono text-sm text-mono-white tabular-nums">
            T+{(currentT / 1000).toFixed(1)}s / {(duration / 1000).toFixed(0)}s
          </div>
        </div>

        {/* Scrub bar with event markers */}
        <div className="relative">
          <input
            type="range"
            min={0}
            max={duration}
            step={100}
            value={currentT}
            onChange={(e) => { setCurrentT(Number(e.target.value)); setPlaying(false); }}
            className="w-full"
          />
          <div className="relative h-6 mt-1 pointer-events-none">
            {data.events.map((e, i) => {
              const left = (e.t_ms / duration) * 100;
              const color = e.action_type.startsWith('system_') ? '#ff4d4d' :
                            e.action_type === 'shock' ? '#ff4d4d' :
                            e.action_type === 'drug_admin' ? '#ffb84d' :
                            e.action_type === 'cpr_pulse' || e.action_type === 'start_compressions' ? '#1aff9c' :
                            '#5be1ff';
              return (
                <div
                  key={i}
                  className="absolute top-0 w-1 h-2"
                  style={{ left: `${left}%`, backgroundColor: color }}
                  title={`T+${(e.t_ms / 1000).toFixed(0)}s — ${ACTION_LABEL[e.action_type] || e.action_type}`}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Event log + current event focus */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          {focusEvent ? (
            <div className="panel p-4 border-l-4 border-brand-600">
              <p className="text-xs text-mono-white/40 uppercase tracking-widest">Current moment</p>
              <p className="text-lg mt-1">{ACTION_LABEL[focusEvent.action_type] || focusEvent.action_type}</p>
              <p className="text-xs text-mono-white/60 mt-1">
                T+{(focusEvent.t_ms / 1000).toFixed(0)}s · {focusEvent.actor_role || 'sim'}
              </p>
              {focusEvent.action_payload_json && (
                <pre className="text-[10px] text-mono-white/50 mt-2 overflow-x-auto font-mono">{JSON.stringify(typeof focusEvent.action_payload_json === 'string' ? JSON.parse(focusEvent.action_payload_json) : focusEvent.action_payload_json, null, 2)}</pre>
              )}
            </div>
          ) : (
            <div className="panel p-4 text-mono-white/40 italic text-sm">Scrub to an event or press Play to step through the run.</div>
          )}
        </div>
        <div className="panel p-3 max-h-64 overflow-y-auto">
          <p className="text-[10px] text-mono-white/40 uppercase tracking-widest mb-2">Event log ({pastEvents.length}/{data.events.length})</p>
          <div className="space-y-1 text-xs font-mono">
            {data.events.map((e, i) => {
              const past = e.t_ms <= currentT;
              const current = focusEvent === e;
              return (
                <div
                  key={i}
                  onClick={() => setCurrentT(e.t_ms)}
                  className={`cursor-pointer px-2 py-1 rounded flex gap-2 hover:bg-ink-800 ${current ? 'bg-brand-600/30' : past ? '' : 'opacity-40'}`}
                >
                  <span className="w-12 text-mono-white/50 tabular-nums">{(e.t_ms / 1000).toFixed(0)}s</span>
                  <span className={e.action_type.startsWith('system_') ? 'text-alarm-red' : ''}>{ACTION_LABEL[e.action_type] || e.action_type}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
